// Right-side MUI Drawer that surfaces extra context when the user clicks
// a KPI tile or the "View data" buttons. The body is always laid out as
//   [scoped metric stats]  →  [People (sample)]  →  [View all expander]
// so the user can both glance at the headline number and inspect the
// underlying people without leaving the drawer.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import type {
  HrDimension,
  HrEmployeeStatus,
  HrFilterMap,
  HrStatsBreakdownRow,
  HrStatsPeopleScope,
  HrStatsSummaryResponse,
} from '../../types/hrStats';
import { usePeopleBreakdown } from '../../hooks/usePeopleBreakdown';
import { formatNumberFull } from '../../utils/FormatUtils';
import StatTile from './primitives/StatTile';
import HBarRow from './primitives/HBarRow';
import { ACCENTS } from './primitives/peopleInsightsTokens';
import type { DrillContext, KpiMetric } from './drillContextTypes';
import PeopleListSection from './PeopleListSection';

export type { DrillContext, KpiMetric };

// ---------------------------------------------------------------------- props

export interface DrillPanelProps {
  open: boolean;
  ctx: DrillContext | null;
  onClose: () => void;
  pageFilters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;
  summary?: HrStatsSummaryResponse;
  /** Apply a page filter (e.g. drill into a country from inside the panel). */
  onApplyFilter: (dim: HrDimension, value: string) => void;
}

const DrillPanel: React.FC<DrillPanelProps> = ({
  open,
  ctx,
  onClose,
  pageFilters,
  employeeStatus,
  summary,
  onApplyFilter,
}) => {
  // Whether the drawer is showing the full people list (vs the 6-row sample).
  // Reset to false whenever the panel re-opens with a new context.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);
  useEffect(() => {
    setExpanded(false);
  }, [ctx?.kind, (ctx as { metric?: string } | null)?.metric, ctx?.label]);

  const peopleScope = ctx ? peopleScopeFor(ctx) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Sticky header */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{
          flexShrink: 0,
          backgroundColor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 2,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 600,
              display: 'block',
            }}
          >
            {ctx ? prettyKind(ctx) : '—'}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {ctx?.label ?? ''}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Body — switches on ctx.kind. flex:1 + minHeight:0 so the inner
          people list (overflow:auto) gets a bounded height when expanded. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 3,
          py: 2.5,
        }}
      >
        {ctx?.kind === 'world' && <WorldBody summary={summary} />}
        {ctx?.kind === 'country' && (
          <CountryBody
            country={ctx.country}
            pageFilters={pageFilters}
            employeeStatus={employeeStatus}
            onApplyFilter={(dim, value) => {
              onApplyFilter(dim, value);
              onClose();
            }}
          />
        )}
        {ctx?.kind === 'kpi' && <KpiBody metric={ctx.metric} summary={summary} />}

        {/* People (sample) — rendered for any context that maps to a scope. */}
        {peopleScope && (
          <PeopleListSection
            scope={peopleScope}
            pageFilters={
              ctx?.kind === 'country'
                ? { ...pageFilters, country: [ctx.country.key] }
                : pageFilters
            }
            employeeStatus={employeeStatus}
            title={peopleSectionTitle(ctx)}
            expanded={expanded}
            onToggleExpanded={() => setExpanded((v) => !v)}
            enabled={open}
          />
        )}
      </Box>
    </Drawer>
  );
};

/** Map a drill context to the people-list scope to use. */
function peopleScopeFor(ctx: DrillContext): HrStatsPeopleScope | null {
  if (ctx.kind === 'world' || ctx.kind === 'country') return 'active';
  switch (ctx.metric) {
    case 'active':
      return 'active';
    case 'joiners':
      return 'joiners_90d';
    case 'relieved':
      return 'relieved_90d';
    case 'tenure':
    case 'locations':
    case 'org':
      return 'active';
    default:
      return null;
  }
}

/** Subheading shown above the people list. */
function peopleSectionTitle(ctx: DrillContext | null): string {
  if (!ctx) return 'People';
  if (ctx.kind === 'country') return `People in ${ctx.country.label}`;
  if (ctx.kind === 'world') return 'People';
  switch (ctx.metric) {
    case 'joiners':
      return 'Recent joiners (90d)';
    case 'relieved':
      return 'Recently relieved (90d)';
    case 'active':
    default:
      return 'People';
  }
}

// ============================================================== sub-renderers

function prettyKind(ctx: DrillContext): string {
  switch (ctx.kind) {
    case 'world':
      return 'Global view';
    case 'country':
      return 'Country';
    case 'kpi':
      return 'KPI';
  }
}

interface WorldBodyProps {
  summary?: HrStatsSummaryResponse;
}

const WorldBody: React.FC<WorldBodyProps> = ({ summary }) => {
  if (!summary) {
    return <Skeleton variant="rounded" height={160} />;
  }
  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
        }}
      >
        <StatTile label="Active" value={formatNumberFull(summary.active_headcount)} />
        <StatTile label="Locations" value={formatNumberFull(summary.distinct_locations)} />
        <StatTile label="BUs" value={formatNumberFull(summary.distinct_business_units)} />
      </Box>
      <Divider />
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em' }}>
        Workforce summary
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <StatTile label="Joiners 30d" value={formatNumberFull(summary.joiners_30d)} positive />
        <StatTile label="Joiners 90d" value={formatNumberFull(summary.joiners_90d)} positive />
        <StatTile label="Relieved 30d" value={formatNumberFull(summary.relieved_30d)} />
        <StatTile label="Relieved 90d" value={formatNumberFull(summary.relieved_90d)} />
        <StatTile label="Avg tenure" value={`${summary.avg_tenure_years.toFixed(1)} yrs`} />
      </Box>
    </Stack>
  );
};

interface CountryBodyProps {
  country: HrStatsBreakdownRow;
  pageFilters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;
  onApplyFilter: (dim: HrDimension, value: string) => void;
}

const CountryBody: React.FC<CountryBodyProps> = ({
  country,
  pageFilters,
  employeeStatus,
  onApplyFilter,
}) => {
  const locationsQuery = usePeopleBreakdown({
    dimension: 'location',
    filters: { ...pageFilters, country: [country.key] },
    employeeStatus,
  });

  const rows = locationsQuery.data?.rows ?? [];
  const maxCount = rows[0]?.count ?? 0;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <StatTile label="Headcount" value={formatNumberFull(country.count)} />
        <StatTile label="Share" value={`${country.percent.toFixed(1)}%`} />
      </Box>
      <Divider />
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em' }}>
        Locations in {country.label}
      </Typography>
      {locationsQuery.isLoading ? (
        <Stack spacing={1}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          No locations recorded for this country.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {rows.map((row) => (
            <HBarRow
              key={row.key}
              label={row.label}
              count={row.count}
              maxCount={maxCount}
              color={ACCENTS.indigo.main}
              onClick={
                row.key === '__null__' ? undefined : () => onApplyFilter('location', row.key)
              }
              trailing={
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {row.percent.toFixed(1)}%
                </Typography>
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

interface KpiBodyProps {
  metric: KpiMetric;
  summary?: HrStatsSummaryResponse;
}

const KpiBody: React.FC<KpiBodyProps> = ({ metric, summary }) => {
  if (!summary) {
    return <Skeleton variant="rounded" height={160} />;
  }
  switch (metric) {
    case 'active':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <StatTile label="Active" value={formatNumberFull(summary.active_headcount)} />
          <StatTile label="Inactive" value={formatNumberFull(summary.inactive_headcount)} />
          <StatTile label="Total" value={formatNumberFull(summary.total_headcount)} />
        </Box>
      );
    case 'joiners':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
          <StatTile label="Last 30 days" value={formatNumberFull(summary.joiners_30d)} positive />
          <StatTile label="Last 90 days" value={formatNumberFull(summary.joiners_90d)} positive />
        </Box>
      );
    case 'relieved':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
          <StatTile label="Last 30 days" value={formatNumberFull(summary.relieved_30d)} />
          <StatTile label="Last 90 days" value={formatNumberFull(summary.relieved_90d)} />
        </Box>
      );
    case 'tenure':
      return (
        <Stack spacing={1.5}>
          <StatTile label="Average tenure" value={`${summary.avg_tenure_years.toFixed(1)} years`} />
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Buckets
            </Typography>
            <Stack spacing={0.5}>
              {summary.tenure_buckets.map((b) => (
                <Stack
                  key={b.bucket}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1,
                    backgroundColor: '#F8FAFC',
                  }}
                >
                  <Typography variant="body2">{b.bucket}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatNumberFull(b.count)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      );
    case 'locations':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
          <StatTile label="Locations" value={formatNumberFull(summary.distinct_locations)} />
          <StatTile
            label="Headcount per location"
            value={
              summary.distinct_locations > 0
                ? (summary.active_headcount / summary.distinct_locations).toFixed(0)
                : '—'
            }
          />
        </Box>
      );
    case 'org':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
          <StatTile label="Business units" value={formatNumberFull(summary.distinct_business_units)} />
          <StatTile label="Pillars" value={formatNumberFull(summary.distinct_pillars)} />
        </Box>
      );
  }
};

export default DrillPanel;
